import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

/**
 * Internationalization (spec § 9).
 *
 * A plain `t()` lookup, NOT the prototype's MutationObserver.
 *
 * The prototype translated by walking the DOM after render and rewriting text
 * nodes. That cannot be ported: it fights React's reconciliation (React
 * re-renders the node, the observer rewrites it, repeat), and it has no way to
 * tell UI chrome from a supplier's own name — it needed `data-notr` markers
 * everywhere to avoid translating user data. Here, user content is simply never
 * passed through `t()`.
 *
 * THE TABLE IS CODE-SPLIT. 663 strings × 3 languages is ~24KB gzipped, and an
 * English user would otherwise download all of it to use none of it — on an
 * app whose whole premise is cold-booting on a bad connection. So the chunk is
 * fetched only when a non-English language is selected, and until it arrives
 * `t()` returns English, which is correct output rather than a broken state.
 */

const I18nContext = createContext({ lang: 'en', t: (s) => s, ready: true });

export function useT() {
  return useContext(I18nContext);
}

/** Cache of the loaded table, so switching language twice fetches once. */
let table = null;
let loading = null;

function loadTable() {
  if (table) return Promise.resolve(table);
  if (!loading) {
    loading = import('./strings')
      .then((m) => {
        table = m;
        return m;
      })
      .catch(() => {
        // A failed chunk fetch (offline, first switch) must not break the app —
        // English is the fallback and it is a correct one.
        loading = null;
        return null;
      });
  }
  return loading;
}

/** Translate against an already-loaded table. Pure. */
export function translateWith(mod, text, lang) {
  if (!mod || !lang || lang === 'en' || text == null) return text;
  const idx = mod.LANG_INDEX[lang];
  if (idx === undefined) return text;

  const key = String(text).trim();
  if (!key) return text;

  const row = mod.STRINGS[key];
  if (row && row[idx]) {
    // Keep whatever whitespace the caller had around the string.
    return String(text).replace(key, row[idx]);
  }

  // Pattern rules for strings carrying a number or a name (spec § 9).
  for (const [re, repl] of mod.PATTERNS) {
    if (re.test(key) && repl[lang]) {
      return String(text).replace(key, key.replace(re, repl[lang]));
    }
  }
  return text;
}

/**
 * Module-scoped current language, and a plain `tr()` that reads it.
 *
 * `useT()` is the idiomatic React way, but it cannot reach the two places that
 * need translating most: the report builders (`reports/sheets.jsx`, `pdf.js`,
 * `csv.js`) are not components, so they cannot call a hook at all. Rather than
 * run two parallel mechanisms, `tr()` is the one used throughout — the
 * provider keeps `currentLang` in sync and remounts its subtree on a change
 * (see `key` below), so every `tr()` call is re-evaluated with the new
 * language. `useT()` stays exported for the few components that want the
 * `ready` flag.
 */
let currentLang = 'en';

export function tr(text) {
  return translateWith(table, text, currentLang);
}

/** The language `tr()` will translate into. */
export function activeLang() {
  return currentLang;
}

export function I18nProvider({ lang = 'en', children }) {
  const [mod, setMod] = useState(table);

  // Set during render, not in an effect: children render immediately after
  // this line, and they read `currentLang` synchronously via `tr()`. An effect
  // would run too late and paint one frame of the previous language.
  currentLang = lang;

  useEffect(() => {
    if (lang === 'en') return;
    let cancelled = false;
    loadTable().then((m) => {
      if (!cancelled && m) setMod(m);
    });
    return () => {
      cancelled = true;
    };
  }, [lang]);

  const t = useCallback((text) => translateWith(mod, text, lang), [mod, lang]);

  const value = useMemo(
    () => ({ lang, t, ready: lang === 'en' || !!mod }),
    [lang, t, mod],
  );

  /**
   * The key remounts the subtree when the language changes, or when the string
   * table finishes loading. `tr()` reads module state, which React cannot
   * observe, so without this a language switch would leave every already-
   * rendered string in the old language until something else happened to
   * re-render it. Remounting here is cheap and safe: the data store sits
   * ABOVE this provider, so nothing is refetched — only re-rendered.
   */
  return (
    <I18nContext.Provider value={value}>
      <React.Fragment key={`${lang}:${mod ? 't' : '-'}`}>{children}</React.Fragment>
    </I18nContext.Provider>
  );
}

/** Preload the table — used by Settings so switching language feels instant. */
export const preloadStrings = loadTable;

/**
 * How many strings a language covers. Resolves to 1 for English and 0 when the
 * table has not loaded, so a caller can show "—" rather than a wrong number.
 */
export async function coverage(lang) {
  if (lang === 'en') return 1;
  const mod = await loadTable();
  if (!mod) return 0;
  const idx = mod.LANG_INDEX[lang];
  if (idx === undefined) return 0;
  const keys = Object.keys(mod.STRINGS);
  const done = keys.filter((k) => mod.STRINGS[k][idx]).length;
  return keys.length ? done / keys.length : 0;
}
